// src/components/video/VideoGalleryPage.tsx
// Track E7 — gallery of rendered videos with status + preview.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, Ban, CheckCircle, Clock, Download, Film, Filter,
  Loader2, Play, Plus, RefreshCw, Sparkles, Trash2, Video as VideoIcon,
  Wand2, X, XCircle,
} from 'lucide-react';

import {
  ApiError,
  cancelVideo, createVideo, deleteVideo, formatDuration, formatFileSize,
  listTemplates, listVideos, renderVideo,
  type Video, type VideoProvider, type VideoStatus, type VideoTemplate,
} from '@/lib/api_video';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string; duration?: number;
}) => void;

interface Props { addNotification?: Notifier }

const STATUS_META: Record<VideoStatus, { label: string; cls: string; icon: JSX.Element }> = {
  draft:     { label: 'Borrador',  cls: 'bg-white/10 text-white/80',         icon: <AlertCircle size={12} /> },
  pending:   { label: 'Pendiente', cls: 'bg-amber-500/20 text-amber-300',     icon: <Clock size={12} /> },
  rendering: { label: 'Renderizando', cls: 'bg-sky-500/20 text-sky-300',      icon: <Loader2 size={12} className="animate-spin" /> },
  completed: { label: 'Listo',     cls: 'bg-emerald-500/20 text-emerald-300', icon: <CheckCircle size={12} /> },
  failed:    { label: 'Error',     cls: 'bg-red-500/20 text-red-300',         icon: <XCircle size={12} /> },
  cancelled: { label: 'Cancelado', cls: 'bg-zinc-500/20 text-zinc-300',       icon: <Ban size={12} /> },
};

const PROVIDER_META: Record<VideoProvider, { label: string; icon: JSX.Element; cls: string }> = {
  remotion: { label: 'Remotion',  icon: <Film size={12} />,     cls: 'bg-indigo-500/20 text-indigo-300' },
  fal_ai:   { label: 'fal.ai',    icon: <Sparkles size={12} />, cls: 'bg-pink-500/20 text-pink-300' },
  manual:   { label: 'Manual',    icon: <VideoIcon size={12} />, cls: 'bg-white/10 text-white/80' },
};

export function VideoGalleryPage({ addNotification }: Props) {
  const [rows, setRows] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | VideoStatus>('all');
  const [providerFilter, setProviderFilter] = useState<'all' | VideoProvider>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await listVideos({
        status: statusFilter === 'all' ? undefined : statusFilter,
        provider: providerFilter === 'all' ? undefined : providerFilter,
        limit: 100,
      });
      setRows(out.items);
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se cargaron videos',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, providerFilter, addNotification]);

  useEffect(() => { load(); }, [load]);

  // Auto-poll while anything is rendering.
  useEffect(() => {
    if (!rows.some((r) => r.status === 'rendering' || r.status === 'pending')) return;
    const t = setInterval(() => load(), 8000);
    return () => clearInterval(t);
  }, [rows, load]);

  const counts = useMemo(() => {
    const acc: Record<string, number> = { all: rows.length };
    for (const r of rows) acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, [rows]);

  const onAction = async (video: Video, op: 'render' | 'cancel' | 'delete') => {
    setBusy((b) => ({ ...b, [video.id]: op }));
    try {
      if (op === 'render') await renderVideo(video.id);
      if (op === 'cancel') await cancelVideo(video.id);
      if (op === 'delete') {
        if (!window.confirm(`¿Eliminar "${video.title || `video ${video.id}`}"?`)) {
          return;
        }
        await deleteVideo(video.id);
      }
      addNotification?.({
        type: 'success',
        title:
          op === 'render' ? 'Render encolado'
          : op === 'cancel' ? 'Cancelado'
          : 'Eliminado',
      });
      await load();
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se pudo ejecutar',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[video.id]; return n; });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Film className="text-emerald-400" /> Videos
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Renders Remotion (branded) + fal.ai (generativo) + uploads manuales.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="o-btn o-btn-ghost flex items-center gap-2">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <button onClick={() => setShowCreate(true)} className="o-btn o-btn-primary flex items-center gap-2">
            <Plus size={16} /> Nuevo render
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex gap-2 overflow-x-auto">
          {(['all', 'draft', 'pending', 'rendering', 'completed', 'failed'] as const).map((id) => (
            <button
              key={id}
              onClick={() => setStatusFilter(id as any)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                statusFilter === id
                  ? 'bg-white/10 border-white/20 text-white'
                  : 'bg-transparent border-transparent text-gray-400 hover:text-gray-200 hover:bg-white/5'
              }`}
            >
              {id === 'all' ? 'Todos' : STATUS_META[id].label}
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-black/20">
                {counts[id] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Filter size={14} className="text-gray-500" />
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value as any)}
            className="px-3 py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="all">Todos los providers</option>
            <option value="remotion">Remotion</option>
            <option value="fal_ai">fal.ai</option>
            <option value="manual">Manual</option>
          </select>
        </div>
      </div>

      {/* Grid */}
      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin text-emerald-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Film size={40} className="mx-auto mb-3 opacity-50" />
          <p>Sin videos {statusFilter !== 'all' ? 'con este filtro' : 'aún'}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((video) => {
            const st = STATUS_META[video.status];
            const pv = PROVIDER_META[video.provider];
            const op = busy[video.id];
            return (
              <div
                key={video.id}
                className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden flex flex-col"
              >
                <div className="aspect-video bg-black/40 relative flex items-center justify-center">
                  {video.status === 'completed' && video.bunny_url ? (
                    <video
                      src={video.bunny_url}
                      controls
                      className="w-full h-full object-contain"
                      preload="metadata"
                    />
                  ) : (
                    <div className="text-gray-500 flex flex-col items-center gap-2">
                      {st.icon}
                      <span className="text-xs">{st.label}</span>
                    </div>
                  )}
                </div>
                <div className="p-3 flex flex-col gap-2 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-white truncate">
                        {video.title || `Video ${video.id}`}
                      </div>
                      <div className="flex items-center gap-1 flex-wrap mt-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${pv.cls}`}>
                          {pv.icon} {pv.label}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                          {st.icon} {st.label}
                        </span>
                      </div>
                    </div>
                  </div>
                  {video.prompt && (
                    <p className="text-xs text-gray-400 line-clamp-2">{video.prompt}</p>
                  )}
                  {video.error_message && (
                    <p className="text-xs text-red-400 line-clamp-2">{video.error_message}</p>
                  )}
                  <div className="text-xs text-gray-500 flex gap-3 mt-auto pt-2">
                    <span>{formatDuration(video.duration_seconds)}</span>
                    <span>{formatFileSize(video.file_size_bytes)}</span>
                  </div>
                  <div className="flex gap-1 pt-2 border-t border-gray-700">
                    {(video.status === 'draft' || video.status === 'failed') && video.provider !== 'manual' && (
                      <button
                        onClick={() => onAction(video, 'render')}
                        disabled={!!op}
                        className="o-btn o-btn-xs o-btn-primary flex items-center gap-1"
                      >
                        {op === 'render' ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                        Render
                      </button>
                    )}
                    {(video.status === 'pending' || video.status === 'rendering') && (
                      <button
                        onClick={() => onAction(video, 'cancel')}
                        disabled={!!op}
                        className="o-btn o-btn-xs o-btn-ghost flex items-center gap-1"
                      >
                        {op === 'cancel' ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                        Cancelar
                      </button>
                    )}
                    {video.status === 'completed' && video.bunny_url && (
                      <a
                        href={video.bunny_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="o-btn o-btn-xs o-btn-ghost flex items-center gap-1"
                      >
                        <Download size={12} /> Descargar
                      </a>
                    )}
                    <button
                      onClick={() => onAction(video, 'delete')}
                      disabled={!!op}
                      className="o-btn o-btn-xs o-btn-ghost text-red-400 ml-auto"
                    >
                      {op === 'delete' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateVideoDrawer
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); }}
          notify={addNotification}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Create drawer
// ═══════════════════════════════════════════════════════════════

function CreateVideoDrawer({
  onClose, onCreated, notify,
}: {
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  notify?: Notifier;
}) {
  const [provider, setProvider] = useState<VideoProvider>('remotion');
  const [templates, setTemplates] = useState<VideoTemplate[]>([]);
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [propsJson, setPropsJson] = useState('{}');
  const [manualUrl, setManualUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [andRender, setAndRender] = useState(true);

  useEffect(() => {
    if (provider === 'manual') { setTemplates([]); setTemplateId(''); return; }
    listTemplates({ provider, is_active: true, limit: 100 })
      .then((r) => setTemplates(r.items))
      .catch(() => setTemplates([]));
    setTemplateId('');
  }, [provider]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let parsed: Record<string, unknown> = {};
      if (propsJson && propsJson.trim() !== '{}') {
        try { parsed = JSON.parse(propsJson); }
        catch {
          notify?.({ type: 'error', title: 'Props JSON inválido' });
          setSaving(false);
          return;
        }
      }
      const video = await createVideo({
        provider,
        template_id: provider === 'manual' ? null : (templateId === '' ? null : Number(templateId)),
        title,
        prompt,
        props: parsed,
        bunny_url: provider === 'manual' ? manualUrl.trim() : '',
      });
      if (provider !== 'manual' && andRender) {
        try { await renderVideo(video.id); }
        catch (e: any) {
          notify?.({
            type: 'error',
            title: 'Creado pero no renderizado',
            message: e instanceof ApiError ? e.message : e?.message || 'Error',
          });
        }
      }
      notify?.({ type: 'success', title: 'Video creado' });
      await onCreated();
    } catch (e: any) {
      notify?.({
        type: 'error',
        title: 'No se pudo crear',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {/* Header fijo + body scroll + footer fijo abajo — el botón 'Crear'
          nunca queda fuera del viewport, que era la queja anterior de UX. */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-6 py-4 border-b border-gray-700 shrink-0">
          <h3 className="text-xl font-semibold text-white">Nuevo video</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-full hover:bg-white/10 text-white/70"
            title="Cerrar"
          >
            <X size={18} />
          </button>
        </header>
        <form onSubmit={save} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Provider</label>
              <div className="grid grid-cols-3 gap-2">
                {(['remotion', 'fal_ai', 'manual'] as VideoProvider[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setProvider(p)}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      provider === p
                        ? 'bg-white/10 border-white/20 text-white'
                        : 'bg-gray-900 border-gray-700 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {PROVIDER_META[p].label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Título</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Intro primavera"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
              />
            </div>

            {provider !== 'manual' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Template</label>
                  {templates.length === 0 ? (
                    <div className="text-sm text-amber-400">
                      No hay templates activos para {PROVIDER_META[provider].label}.
                      Crea uno en la pestaña Templates.
                    </div>
                  ) : (
                    <select
                      value={templateId}
                      onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                    >
                      <option value="">— Sin template (usa props directos) —</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>
                      ))}
                    </select>
                  )}
                </div>

                {provider === 'fal_ai' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Prompt</label>
                    <textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      rows={3}
                      placeholder="playa al atardecer, drone shot, slow motion"
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Props (JSON)</label>
                  <textarea
                    value={propsJson}
                    onChange={(e) => setPropsJson(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white font-mono text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="andRender"
                    checked={andRender}
                    onChange={(e) => setAndRender(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-900 text-emerald-500 focus:ring-emerald-500"
                  />
                  <label htmlFor="andRender" className="text-sm text-gray-300">
                    Disparar render automáticamente después de crear
                  </label>
                </div>
              </>
            )}

            {provider === 'manual' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  URL de Bunny (video ya subido)
                </label>
                <input
                  value={manualUrl}
                  onChange={(e) => setManualUrl(e.target.value)}
                  placeholder="https://calaita.b-cdn.net/..."
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:border-emerald-500 focus:outline-none font-mono text-sm"
                  required
                />
              </div>
            )}

          </div>
          {/* Footer sticky — siempre visible; el botón principal nunca
              se pierde aunque el form crezca. */}
          <footer className="flex justify-end gap-3 px-6 py-4 border-t border-gray-700 bg-gray-800 shrink-0">
            <button type="button" onClick={onClose} className="o-btn o-btn-ghost">Cancelar</button>
            <button type="submit" disabled={saving || (provider === 'manual' && !manualUrl.trim())} className="o-btn o-btn-primary flex items-center gap-2">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
              {provider === 'manual' ? 'Guardar' : (andRender ? 'Crear y generar' : 'Crear borrador')}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

export default VideoGalleryPage;
